/**
 * Kill-switches for fast-drive `browser_scan` side effects.
 *
 * Two independently-gated behaviors:
 *  1. **Ephemeral-ref stamping** — when no durable locator exists, scan stamps a
 *     short-lived `data-fdref` attribute. That stamp is scan's only DOM mutation
 *     and runs against fast-drive's raw-DOM invariant, so it gets a kill-switch.
 *  2. **run_code click-failure self-repair** — on every failed click, a bounded
 *     scan runs to hand back a better locator. It's cheap and reveal-only, but
 *     because it fires on a hot path it gets its own switch to disable in prod.
 *
 * Like {@link module:expensive-gate}, browser-core cannot read the knob directly
 * (it's an apps/api Effect service and runs in the worker/CLI too). The host
 * reads the knobs on its refresh interval and pushes values here.
 *
 * **Wiring status:** these are per-process module singletons. The API host and
 * the job worker share the apps/api entrypoint (`server.ts`), so both are wired
 * via `cpu-offload/config.ts` — this is where prod workflow execution and
 * cache-replay run, so both knobs are honored there.
 *
 * The standalone `canary-cli` daemon wires NEITHER knob, so both default ON there.
 * Note this is NOT symmetric between the two switches:
 *  - **Stamping** additionally requires `allowStamp`, which the daemon's
 *    dispatcher path never sets — so stamping is inert in the daemon regardless
 *    of the switch.
 *  - **Click-repair** fires from `PlaywrightClient.executeRunCode` and is gated
 *    ONLY by `isFastDriveClickRepairEnabled()`, independent of any toolset — so it
 *    runs with the default (ON) in the daemon and the knob cannot disable it
 *    there. That's acceptable because the daemon is a local/interactive dev tool
 *    (no prod knob source), and self-repair only improves the error message. If a
 *    prod-facing process ever runs `executeRunCode` without wiring this gate, wire
 *    `configureFastDriveClickRepair` in its bootstrap.
 *
 * @module fast-drive-scan-gate
 */

let stampingEnabled = true;
let clickRepairEnabled = true;

/**
 * Enable/disable the ephemeral-ref stamp fallback. Wire from
 * `browser.fast_drive.scan.stamping`. Safe to call repeatedly (knob refresh).
 */
export function configureFastDriveScanStamping(enabled: boolean): void {
  stampingEnabled = enabled;
}

/** Whether scan may stamp `data-fdref` as a last-resort handle. */
export function isFastDriveScanStampingEnabled(): boolean {
  return stampingEnabled;
}

/**
 * Enable/disable the run_code click-failure self-repair scan. Wire from
 * `browser.fast_drive.scan.click_repair`.
 */
export function configureFastDriveClickRepair(enabled: boolean): void {
  clickRepairEnabled = enabled;
}

/** Whether a failed run_code click triggers a self-repair scan. */
export function isFastDriveClickRepairEnabled(): boolean {
  return clickRepairEnabled;
}

/** @internal test seam — reset both gates to their defaults (enabled). */
export function _resetFastDriveScanGatesForTest(): void {
  stampingEnabled = true;
  clickRepairEnabled = true;
}
